import * as R from 'ramda'
import * as db from '../db/db'
import * as i18next from 'i18next'
import { AppSession } from 'app/app/api/auth/auth'
import { escapeRegExp, filterByProperty, getOpportunityFromAccountsById } from 'app/app/common/common-utils'
import {
  OpportunitySfId,
  Building,
  CustomerSfId,
  EquipmentCategory,
  FrontlineConfigJson,
  Offer,
  Opportunity,
  OpportunityBuilding,
  OpportunityCustomer,
  ClientFrontlineConfig,
  AppConfig,
  ClientProfile,
  OfferingQuestions
} from 'app/types/types'
import {
  BuildingEquipment,
  CustomerModel,
  FilterType,
  OpportunitiesDataType,
  OpportunitiesIndexSortBy,
  OpportunityFilterOption,
  OpportunityModel,
  OpportunitySortOption,
  OpportunityStatus
} from 'app/types/ui/opportunity'
import { offerIsCompleted } from 'app/app/common/offer-state-predicates'
import { t } from 'i18next'
import { CustomerData } from 'app/types/backend-api'
import createEmptyOffer from '../nemo/offer/create-empty-offer'
import { updateOfferSalesforceData } from '../nemo/offer/salesforce-sync'
import { Icons } from './client-constants'
import { clearSfCache, shareOpportunities } from 'app/services/client/client-api'
import { hideAlert, showAlert, updateIsLoadingState } from '../redux/features/notification/alert-slice'
import { AppDispatch } from '../redux/Store'
import { capitalizeIfEnglish, getEquipmentText, resolveStatus } from './utils'
import { setCurrentAccount } from '../redux/features/customers/current-account-slice'
import { updateOfferConfiguration } from '../nemo/offer/reset'
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'
import { setOfferData } from '../redux/features/currentOffer/current-offer-slice'
import { updateOfferAsync } from '../redux/features/currentOffer/update-offer-async'
import { updateCustomerState } from '../redux/features/customers/customer-slice'
import { handleUIError } from 'app/services/client/error-handling'
import { notify } from 'app/app/bugsnag/bugsnag.client'
import { useNavigate } from '../hooks/useNavigate'
import { navigateToPage } from '../redux/features/navigation/navigateToPage'

const categoryOptions = [
  {
    key: 'New KONE Equipment',
    value: (i18n: i18next.i18n) => i18n.t('salesforce.opportunity.opportunityCategory.NewKONEEquipment')
  },
  {
    key: 'Renegotiation',
    value: (i18n: i18next.i18n) => i18n.t('salesforce.opportunity.opportunityCategory.Renegotiation')
  },
  {
    key: 'Maintained by Competitors',
    value: (i18n: i18next.i18n) =>
      capitalizeIfEnglish(i18n.t('salesforce.opportunity.opportunityCategory.MaintainedbyCompetitors'))
  },
  {
    key: 'Owner/No Maintenance',
    value: (i18n: i18next.i18n) => i18n.t('salesforce.opportunity.opportunityCategory.OwnerNoMaintenance')
  },
  {
    key: 'CTU, LIFS and Other',
    value: (i18n: i18next.i18n) => i18n.t('salesforce.opportunity.opportunityCategory.CTULIFSandOther')
  }
]

const statusOptions = [
  {
    key: OpportunityStatus.BeginPrepare,
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.refresh.beginPrepareStatus'))
  },
  {
    key: OpportunityStatus.PrepareInProgress,
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.refresh.newPrepareInProgress'))
  },
  {
    key: OpportunityStatus.GroupingInProgress,
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.refresh.newGroupingInProgress'))
  },
  {
    key: OpportunityStatus.DiscountApprovalPending,
    value: (i18n: i18next.i18n) =>
      capitalizeIfEnglish(
        i18n.t('offerWizard.discountApproval.title') + ' ' + i18n.t('offerWizard.discountApproval.pending')
      )
  },
  {
    key: OpportunityStatus.DiscountApprovalRejected,
    value: (i18n: i18next.i18n) =>
      capitalizeIfEnglish(
        i18n.t('offerWizard.discountApproval.title') + ' ' + i18n.t('offerWizard.discountApproval.rejected')
      )
  },
  {
    key: OpportunityStatus.OfferStatusIncomplete,
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('offerWizard.status.incomplete'))
  },
  {
    key: OpportunityStatus.OfferInProgress,
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.offerInProgress'))
  },
  {
    key: OpportunityStatus.OfferCreated,
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.offerCreated'))
  },
  {
    key: OpportunityStatus.SentToSap,
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.sentToSap'))
  }
]

export const filterOptions: OpportunityFilterOption[] = [
  {
    key: 'filterCategory',
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.filterCategory')),
    filterType: 'undefined' as FilterType,
    disabled: true
  },
  ...categoryOptions.map(opt => ({
    key: opt.key,
    value: opt.value,
    filterType: 'category' as FilterType
  })),
  {
    key: 'filterStatus',
    value: (i18n: i18next.i18n) => capitalizeIfEnglish(i18n.t('customers.filterStatus')),
    filterType: 'undefined' as FilterType,
    disabled: true
  },

  ...statusOptions.map(opt => ({
    key: opt.key,
    value: opt.value,
    filterType: 'status' as FilterType
  }))
]

export const sortOptions: OpportunitySortOption[] = [
  {
    key: 'sortAlphabetical',
    value: 'customers.refresh.alphabetical',
    sortType: 'undefined' as OpportunitiesIndexSortBy,
    sortOrderDescending: false,
    disabled: true
  },
  {
    key: 'aToz',
    value: 'customers.refresh.atoz',
    sortType: 'name' as OpportunitiesIndexSortBy,
    sortOrderDescending: false
  },
  {
    key: 'zToa',
    value: 'customers.refresh.ztoa',
    sortType: 'name' as OpportunitiesIndexSortBy,
    sortOrderDescending: true
  },
  {
    key: 'sortDate',
    value: 'customers.refresh.date',
    sortType: 'undefined' as OpportunitiesIndexSortBy,
    sortOrderDescending: false,
    disabled: true
  },

  {
    key: 'sortAscending',
    value: 'customers.refresh.ascending',
    sortType: 'dueDate' as OpportunitiesIndexSortBy,
    sortOrderDescending: false
  },
  {
    key: 'sortDescending',
    value: 'customers.refresh.descending',
    sortType: 'dueDate' as OpportunitiesIndexSortBy,
    sortOrderDescending: true
  }
]

const filterFn = {
  undefined: (_: any) => false,
  category: (filterKey: string, opportunity: OpportunityModel) => filterKey === opportunity.category,
  status: (filterKey: string, _opportunity: OpportunityModel, offer?: Offer, frontlineConfig?: ClientFrontlineConfig) =>
    resolveStatus(offer, frontlineConfig) === OpportunityStatus[filterKey as keyof typeof OpportunityStatus]
}

export const validateInitialParams = (
  opportunityId: string,
  offerId: string | undefined,
  accounts: OpportunityCustomer[],
  offers: Offer[]
): boolean => {
  if (!opportunityId) {
    return false
  }
  const isValid = (param: string | undefined, model: any) => !param || !!model
  const opportunity = getOpportunityFromAccountsById(opportunityId, accounts)
  const offer = offerId ? offers.find(o => o.id === offerId || o.opportunityId === offerId) : undefined

  if (!isValid(opportunityId, opportunity) || !isValid(offerId, offer)) {
    return false
  }
  return true
}

export const onOpportuitySelect = (
  frontlineConfig: ClientFrontlineConfig,
  dispatch: any,
  opportunityId: string,
  profile: ClientProfile,
  accountId: string,
  router: ReturnType<typeof useNavigate>
) => {
  if (opportunityId && accountId) {
    Promise.all([db.fetchAccountPromise(accountId), db.getOfferByOpportunityIdPromise(opportunityId)])
      .then(async ([opportunityCustomer, offer]) => {
        dispatch(setCurrentAccount(opportunityCustomer as OpportunityCustomer))
        if (offer) {
          const { dependencies, ...rest } = frontlineConfig
          const clientConfig: ClientFrontlineConfig = {
            ...rest,
            dependencies,
            offeringQuestions: {} as OfferingQuestions
          }
          const updatedOffer = offerIsCompleted(offer) ? offer : updateOfferConfiguration(clientConfig, offer)
          dispatch(setOfferData(updatedOffer))
          await db.updateOfferPromise(updatedOffer)
          dispatch(navigateToPage({ router, offer: updatedOffer }))
        } else {
          const customerccount = opportunityCustomer as OpportunityCustomer
          const opportunity = customerccount.opportunities.find(o => o.id === opportunityId) as Opportunity
          const customer = R.dissoc('opportunities', customerccount)
          const assessmentMethod = null
          const emptyOffer = createEmptyOffer(customer, opportunity, profile, frontlineConfig, assessmentMethod)
          const offerWithSFData = updateOfferSalesforceData(opportunity, customer, frontlineConfig)(emptyOffer)
          await dispatch(updateOfferAsync(_ => offerWithSFData))
          dispatch(navigateToPage({ router, offer: offerWithSFData }))
        }
      })
      .catch(error => {
        console.error('Error loading opportunity:', error)
      })
  }
  // ToDo: check if we need to add page tracking here
}

export const getOfferByOpportunityId = async (opportunityId: string) =>
  await db.getOfferByOpportunityIdPromise(opportunityId)

export const getCustomerOpportunityData = async (
  flConfig: FrontlineConfigJson | undefined,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction> | undefined
) => {
  if (!flConfig) {
    console.error('FrontLineConfig is not available in the state')
    return
  }

  return await Promise.all([db.getOffersPromise(), db.fetchAccountsPromise()]).then(([offers, customers]) => {
    const dueDates: Record<CustomerSfId, Date> = {}
    const offerByOpportunityId: Record<OpportunitySfId, Offer> = {}

    offers.forEach(offer => {
      offerByOpportunityId[offer.opportunityId] = offer
    })

    const maxFutureFuture = new Date(new Date().setFullYear(new Date().getFullYear() + 10))
    customers.forEach(customer => {
      dueDates[customer.id] = customer.opportunities.reduce((acc, opportunity) => {
        const closeDate = new Date(opportunity.closeDate)
        return closeDate < acc ? closeDate : acc
      }, maxFutureFuture)
    })
    if (dispatch) {
      dispatch(updateCustomerState({ customers, offers }))
    }
    return {
      dueDates,
      filteredCustomers: customers,
      initialCustomers: customers,
      offers,
      offerByOpportunityId
    }
  })
}

export const getCustomerModels = (
  customers: OpportunityCustomer[],
  dueDates: Record<string, Date>,
  offerByOpportunityId: Record<string, Offer>
) => {
  if (!customers) {
    return [] as CustomerModel[]
  }

  const allCustomerModels = customers.map(customer => ({
    id: customer.id,
    name: customer.name,
    dueDate: dueDates[customer.id],
    numberOfContracts: customer.contractCount,
    opportunities: customer.opportunities.map(opportunity => {
      const offer = offerByOpportunityId[opportunity.id]

      const buildingEquipmensArr: BuildingEquipment[] = []
      buildingEquipmensArr.push({
        element: Icons.location,
        count: getBuildingCount(opportunity, offer),
        elementDescription: 'customers.refresh.location'
      })

      const elevatorCount = getEquipmentCount('elevator', opportunity, offer)
      const escalatorCount = getEquipmentCount('escalator', opportunity, offer)

      if (elevatorCount > 0) {
        const elevatorText = getEquipmentText('elevator', elevatorCount)
        buildingEquipmensArr.push({
          element: Icons.elevator,
          count: elevatorCount,
          elementDescription: elevatorText
        })
      }
      if (escalatorCount > 0) {
        const escalatorText = getEquipmentText('escalator', escalatorCount)
        buildingEquipmensArr.push({
          element: Icons.escalator,
          count: escalatorCount,
          elementDescription: escalatorText
        })
      }

      const ownerName = `${opportunity.ownerFirstName || ''} ${opportunity.ownerLastName || ''}`.trim()

      return {
        id: opportunity.id,
        customerId: customer.id,
        name: opportunity.name,
        ownerId: opportunity.ownerId,
        owner: ownerName,
        category: opportunity.opportunityCategory,
        dueDate: new Date(opportunity.closeDate),
        offer,
        buildingEquipments: buildingEquipmensArr
      }
    })
  }))

  return allCustomerModels
}

export const filterBySearchTerm = (
  searchTerm: string,
  customers: CustomerModel[],
  session: AppSession,
  filterOrSearchActive: boolean
) => {
  const regex = new RegExp(escapeRegExp(searchTerm), 'i')
  const filteredCustomers = customers
    .map(customer => {
      if (regex.test(customer.name || '')) {
        return customer
      } else {
        const opportunities = customer.opportunities.filter(opportunity => regex.test(opportunity.name))
        return { ...customer, opportunities }
      }
    })
    .filter(({ opportunities }) => opportunities.length > 0)

  const { profile } = session

  const myOpportunities = filteredCustomers.reduce((acc, customer) => {
    const opportunities = customer.opportunities.filter(opportunity => opportunity.ownerId === profile.user_id)
    return opportunities.length > 0 ? acc.concat([{ ...customer, opportunities }]) : acc
  }, new Array<CustomerModel>())

  const sharedOpportunities = filteredCustomers.reduce((acc, customer) => {
    const opportunities = customer.opportunities.filter(
      opportunity =>
        opportunity.offer && opportunity.offer.opportunityShares.length > 0 && opportunity.ownerId !== profile.user_id
    )
    return opportunities.length > 0 ? acc.concat([{ ...customer, opportunities }]) : acc
  }, new Array<CustomerModel>())

  // If we return an array (empty or filled) that means that there could be results.
  // When we return null it means that no results are available and this is not due search filter
  return {
    myOpportunities: myOpportunities.length > 0 || filterOrSearchActive ? myOpportunities : null,
    sharedOpportunities: sharedOpportunities.length > 0 || filterOrSearchActive ? sharedOpportunities : null,
    status: 'succeeded'
  } as OpportunitiesDataType
}

export const filterByOpportunityFilter = (
  customers: CustomerModel[],
  offerByOpportunityId: Record<string, Offer>,
  frontlineConfig: ClientFrontlineConfig,
  selectedOpportunityFilters: OpportunityFilterOption[]
) => {
  if (selectedOpportunityFilters.length === 0) {
    return customers
  }

  const selectedOpportunityFiltersByType = R.toPairs(R.groupBy(filter => filter.filterType, selectedOpportunityFilters))

  return customers
    .map(customer => {
      const opportunities = customer.opportunities.filter(opportunity => {
        const offer = offerByOpportunityId[opportunity.id]

        return selectedOpportunityFiltersByType.every(([_, filters]) =>
          filters.some(filter => filterFn[filter.filterType](filter.key, opportunity, offer, frontlineConfig))
        )
      })
      return { ...customer, opportunities }
    })
    .filter(({ opportunities }) => opportunities.length > 0)
}

const sortBasedOnSelectedOption = (
  data: CustomerModel | OpportunityModel,
  sortBy: OpportunitiesIndexSortBy
): number | Date | string => {
  switch (sortBy) {
    case 'dueDate':
      return data.dueDate
    case 'name':
      return data.name || ''
    default:
      return ''
  }
}

export const sortBySelectedOption = (
  customers: CustomerModel[],
  sortByOption: OpportunitySortOption | undefined
): CustomerModel[] => {
  if (!sortByOption || !sortByOption.sortType) {
    return customers
  }

  const sortCustomersBySortType = R.sortBy(
    customer => sortBasedOnSelectedOption(customer, sortByOption.sortType),
    customers
  )

  const sortCustomersAscOrDesc = sortByOption.sortOrderDescending
    ? sortCustomersBySortType.reverse()
    : sortCustomersBySortType

  const finalSortedCustomers = sortCustomersAscOrDesc.map(customer => {
    const opportunitiesArr = R.sortBy(
      opportunity => sortBasedOnSelectedOption(opportunity, sortByOption.sortType),
      customer.opportunities
    )

    const sortOpportunitiesAscOrDesc = sortByOption.sortOrderDescending ? opportunitiesArr.reverse() : opportunitiesArr

    return { ...customer, opportunities: sortOpportunitiesAscOrDesc }
  })

  return finalSortedCustomers
}

export const getBuildingCount = (opportunity: Opportunity, offer?: Offer) =>
  offer && offer.buildings ? offer.buildings.length : opportunity.buildings.length

export const getEquipmentCount = (equipmentCategory: EquipmentCategory, opportunity: Opportunity, offer?: Offer) =>
  offer
    ? getEquipmentCountForOffer(offer.buildings, equipmentCategory)
    : getEquipmentCountForOpportunity(opportunity.buildings, equipmentCategory)

/* For Offer  */
export const getEquipmentCountForOffer = (buildings: Building[], equipmentCategory: EquipmentCategory) =>
  buildings.map((building: Building) => getBuildingEquipmentCountForOffer(building, equipmentCategory)).reduce(R.add, 0)

const getBuildingEquipmentCountForOffer = (building: Building, equipmentCategory: EquipmentCategory) => {
  const equipmentDetails = R.pathOr([], ['details', 'equipment'], building)
  return filterByProperty(equipmentDetails, 'equipmentCategory', equipmentCategory).length
}

/* For Opportunity */

export const getEquipmentCountForOpportunity = (
  buildings: OpportunityBuilding[],
  equipmentCategory: EquipmentCategory
): number =>
  buildings
    .map((building: OpportunityBuilding) => getBuildingEquipmentCountForOpportunity(building, equipmentCategory))
    .reduce(R.add, 0)

const getBuildingEquipmentCountForOpportunity = (
  building: OpportunityBuilding,
  equipmentCategory: EquipmentCategory
): number => {
  const equipmentDetails = R.propOr([], 'equipment', building)
  return filterByProperty(equipmentDetails, 'equipmentCategory', equipmentCategory).length
}

export const constructOfferObject = (
  customerData: CustomerData,
  configData: AppConfig,
  custId: string,
  oppId: string
) => {
  const { customers, offers } = customerData
  const { profile, frontLineConfig } = configData
  const opportunityCustomer = customers?.find(customer => customer.id === custId)
  if (!opportunityCustomer || !profile || !frontLineConfig) {
    return
  }
  const opportunity = opportunityCustomer.opportunities.find(({ id }) => id === oppId)
  if (!opportunity) {
    return
  }
  const offer = offers?.find(opportunityOffer => opportunityOffer.opportunityId === oppId)
  return offer || createNewOffer(opportunityCustomer, opportunity, profile, frontLineConfig)
}

const createNewOffer = (
  opportunityCustomer: OpportunityCustomer,
  opportunity: Opportunity,
  profile: ClientProfile,
  flConfig: ClientFrontlineConfig
): Offer => {
  const emptyOffer = createEmptyOffer(opportunityCustomer, opportunity, profile, flConfig, null)
  return updateOfferSalesforceData(opportunity, opportunityCustomer, flConfig)(emptyOffer)
}

export const importOpportunities = async (dispatch: AppDispatch, opportunityIds: string[], isShare: boolean) => {
  if (opportunityIds.length > 0) {
    try {
      await shareOpportunities({ opportunityIds, isShare })
      await clearSfCache()
      window.location.reload()
    } catch (error: any) {
      console.log(error)
      notify(error)
      handleUIError(dispatch, error)
    }
  }
}

export const deleteOpportunities = (selectedOpportunityIdsForUnShare: string[], dispatch: AppDispatch) => {
  dispatch(
    showAlert({
      id: 'delete-shared-opportunities-alert',
      title: t('customers.unshareOpportunity.confirmDialogTitle'),
      description: t('customers.unshareOpportunity.confirmDialogText'),
      type: 'warning',
      onSecondaryClick: () => dispatch(hideAlert()),
      onPrimaryClick: async () => {
        dispatch(updateIsLoadingState(true))
        await importOpportunities(dispatch, selectedOpportunityIdsForUnShare, false)
        dispatch(updateIsLoadingState(false))
      }
    })
  )
}
